import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Table, Button, Space, Tag, Select, DatePicker, message, Modal, Spin, Typography, Upload, Progress, Alert, Drawer, Dropdown } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlayCircle, faPauseCircle, faTrash, faEye, faCheckCircle, faTimesCircle, faExclamationTriangle, faInbox, faBolt, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { callAnalyticsAPI } from '../../../services/callAnalytics';
import RecordingDetailsModal from './RecordingDetailsModal';

const CallRecordingsTable = forwardRef(({ campaigns, onFiltersChange }, ref) => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    dateRange: null,
    campaignName: null,
    category: null,
    transcriptionStatus: null
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]); // Track files being uploaded
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const audioRef = useRef(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openFilterDrawer: () => setFilterDrawerVisible(true),
    openUploadModal: () => setUploadModalVisible(true),
    refresh: () => loadRecordings(),
    hasActiveFilters: () => !!(filters.dateRange || filters.campaignName || filters.category || filters.transcriptionStatus)
  }));

  useEffect(() => {
    loadRecordings();
  }, [pagination.current, pagination.pageSize, filters]);

  // Notify parent when filters change
  useEffect(() => {
    if (onFiltersChange) {
      const hasActive = !!(filters.dateRange || filters.campaignName || filters.category || filters.transcriptionStatus);
      onFiltersChange(hasActive);
    }
  }, [filters, onFiltersChange]);

  // Auto-refresh table when recordings are processing
  useEffect(() => {
    const hasProcessing = recordings.some(r => r.transcriptionStatus === 'processing');
    if (!hasProcessing) return;

    const pollInterval = setInterval(() => {
      console.log('[Table Polling] Refreshing recordings...');
      loadRecordings();
    }, 10000); // Poll every 10 seconds

    return () => {
      console.log('[Table Polling] Clearing interval');
      clearInterval(pollInterval);
    };
  }, [recordings]);

  const loadRecordings = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize
      };

      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }

      if (filters.campaignName) {
        params.campaignName = filters.campaignName;
      }

      if (filters.category) {
        params.category = filters.category;
      }

      if (filters.transcriptionStatus) {
        params.transcriptionStatus = filters.transcriptionStatus;
      }

      const data = await callAnalyticsAPI.getRecordings(params);
      setRecordings(data.recordings);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total
      }));
    } catch (error) {
      message.error('Failed to load recordings');
      console.error('Load recordings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
  };

  const handlePlayPause = async (recording) => {
    if (playingId === recording._id) {
      // Pause current
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      // Stop previous and play new
      audioRef.current?.pause();
      
      try {
        // Get recording with signed URL
        const recordingData = await callAnalyticsAPI.getRecording(recording._id);
        
        if (recordingData && recordingData.playbackUrl) {
          audioRef.current = new Audio(recordingData.playbackUrl);
          audioRef.current.play();
          setPlayingId(recording._id);

          audioRef.current.onended = () => {
            setPlayingId(null);
          };
        } else {
          message.error('Playback URL not available');
        }
      } catch (error) {
        message.error('Failed to load recording');
        console.error('Playback error:', error);
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await callAnalyticsAPI.deleteRecording(id);
      message.success('Recording deleted successfully');
      loadRecordings();
    } catch (error) {
      message.error('Failed to delete recording');
      console.error('Delete error:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select recordings to delete');
      return;
    }

    Modal.confirm({
      title: `Delete ${selectedRowKeys.length} recordings?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await callAnalyticsAPI.bulkDeleteRecordings(selectedRowKeys);
          message.success(`Deleted ${selectedRowKeys.length} recordings`);
          setSelectedRowKeys([]);
          loadRecordings();
        } catch (error) {
          message.error('Failed to delete recordings');
          console.error('Bulk delete error:', error);
        }
      }
    });
  };

  const handleBulkAssignCampaign = async (campaignName) => {
    if (!campaignName) {
      return;
    }

    try {
      const result = await callAnalyticsAPI.bulkAssignCampaign(selectedRowKeys, campaignName);
      
      if (result.success) {
        message.success(`Assigned ${result.modifiedCount} recordings to campaign "${campaignName}"`);
        setSelectedRowKeys([]);
        loadRecordings();
      } else {
        message.error(result.error || 'Failed to assign campaign');
      }
    } catch (error) {
      message.error('Failed to assign campaign');
      console.error('Bulk assign campaign error:', error);
    }
  };

  const handleBulkTranscribe = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select recordings to transcribe');
      return;
    }

    const selectedRecordings = recordings.filter(r => selectedRowKeys.includes(r._id));
    
    // Filter out recordings shorter than 5 seconds
    const validRecordings = selectedRecordings.filter(r => (r.duration || 0) >= 5);
    const tooShortRecordings = selectedRecordings.filter(r => (r.duration || 0) < 5);
    
    if (validRecordings.length === 0) {
      message.warning('All selected recordings are too short (< 5 seconds) to transcribe');
      return;
    }
    
    const totalMinutes = validRecordings.reduce((sum, r) => sum + (r.duration || 0), 0) / 60;
    const estimatedCost = (totalMinutes * 0.024).toFixed(2);

    Modal.confirm({
      title: 'Confirm Batch Transcription',
      width: 520,
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>You are about to transcribe <strong>{validRecordings.length} recording{validRecordings.length !== 1 ? 's' : ''}</strong>.</p>
          
          {tooShortRecordings.length > 0 && (
            <Alert
              message={`${tooShortRecordings.length} recording${tooShortRecordings.length !== 1 ? 's' : ''} skipped (< 5 seconds)`}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
          
          <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
            <p style={{ margin: '4px 0' }}><strong>Total Duration:</strong> {totalMinutes.toFixed(1)} minutes</p>
            <p style={{ margin: '4px 0' }}><strong>Estimated Cost:</strong> ${estimatedCost}</p>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <p style={{ fontWeight: '500', marginBottom: '8px' }}>What will happen:</p>
            <ul style={{ paddingLeft: '20px', margin: '0' }}>
              <li>Recordings will be sent to AWS Transcribe</li>
              <li>AI will automatically categorize calls (Qualified Lead, Not Interested, etc.)</li>
              <li>Categories with 90%+ confidence will be auto-confirmed</li>
              <li>Lower confidence categories can be reviewed manually</li>
            </ul>
          </div>

          <p style={{ color: '#666', fontSize: '13px', marginTop: '16px' }}>
            This process runs in the background and may take several minutes depending on recording length.
          </p>
        </div>
      ),
      okText: 'Start Transcription',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const validRecordingIds = validRecordings.map(r => r._id);
          const result = await callAnalyticsAPI.batchTranscribe(validRecordingIds);

          if (result.success) {
            const { results } = result;
            if (results.successful > 0) {
              message.success(`Successfully started transcription for ${results.successful} recordings`);
            }
            if (results.failed > 0) {
              message.warning(`${results.failed} recordings failed to start`);
            }
          } else {
            message.warning(result.message || 'Some recordings could not be transcribed');
          }

          setSelectedRowKeys([]);
          loadRecordings();
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Failed to start batch transcription';
          message.error(errorMessage);
          console.error('Batch transcribe error:', error);
        }
      }
    });
  };

  const handleViewDetails = (recording) => {
    setSelectedRecording(recording);
    setDetailsModalVisible(true);
  };

  const handleDetailsModalClose = () => {
    setDetailsModalVisible(false);
    setSelectedRecording(null);
  };

  const handleRecordingUpdate = () => {
    loadRecordings();
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Please select files to upload');
      return;
    }

    // Create placeholder rows for each file
    const placeholders = fileList.map((file, index) => ({
      _id: `uploading-${Date.now()}-${index}`,
      filename: file.name,
      date: new Date(),
      phoneNumber: '-',
      extension: '-',
      duration: 0,
      transcriptionStatus: 'uploading',
      category: 'uncategorized',
      aiSuggestedCategory: 'uncategorized',
      isPlaceholder: true
    }));

    // Add placeholders to the beginning of the recordings list
    setRecordings(prev => [...placeholders, ...prev]);
    setUploadingFiles(placeholders.map(p => p._id));

    // Close modal immediately
    setUploadModalVisible(false);
    setUploading(true);

    try {
      const result = await callAnalyticsAPI.uploadRecordings(fileList);

      // Show summary message
      const { successful, duplicates, failed } = result.results;
      if (successful > 0 && failed === 0 && duplicates === 0) {
        message.success(`Successfully uploaded ${successful} recordings`);
      } else if (successful > 0) {
        message.warning(`Uploaded ${successful} recordings. ${duplicates} duplicates, ${failed} failed.`);
      } else {
        message.error('Failed to upload recordings');
      }

      // Clear file list and placeholders
      setFileList([]);
      setUploadingFiles([]);
      
      // Refresh the table to show actual data
      loadRecordings();

    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to upload recordings');
      console.error('Upload error:', error);
      
      // Remove placeholders on error
      setRecordings(prev => prev.filter(r => !r.isPlaceholder));
      setUploadingFiles([]);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadResults(null);
    }
  };

  const columns = [
    {
      title: 'Date/Time',
      dataIndex: 'date',
      key: 'date',
      width: '12%',
      sorter: true,
      render: (date, record) => (
        <div>
          <div>{moment(date).format('MMM DD, YYYY')}</div>
          {record.time && (
            <div style={{ fontSize: '12px', color: '#666' }}>{record.time}</div>
          )}
        </div>
      )
    },
    {
      title: 'Campaign',
      dataIndex: 'campaignName',
      key: 'campaignName',
      width: '15%',
      render: (name) => name ? <Tag color="blue">{name}</Tag> : <Tag>Unknown</Tag>
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: '12%',
      render: (phone) => phone || '-'
    },
    {
      title: 'Extension',
      dataIndex: 'extension',
      key: 'extension',
      width: '8%',
      align: 'center',
      render: (ext) => ext ? <Tag color="green">{ext}</Tag> : '-'
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: '8%',
      align: 'center',
      render: (duration) => {
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      }
    },
    {
      title: 'Category',
      dataIndex: 'aiSuggestedCategory',
      key: 'aiSuggestedCategory',
      width: '12%',
      render: (aiCategory, record) => {
        if (!aiCategory) {
          return (
            <Tag 
              color="default"
              style={{ borderRadius: '12px' }}
            >
              Not analyzed
            </Tag>
          );
        }
        
        const confidence = record.aiCategoryConfidence || 0;
        let badgeColor = 'red';
        if (confidence >= 80) badgeColor = 'black';
        else if (confidence >= 50) badgeColor = 'orange';

        return (
          <Space direction="vertical" size={0}>
            <Tag 
              color={badgeColor}
              style={{ borderRadius: '12px' }}
            >
              {aiCategory}
            </Tag>
            <span style={{ fontSize: '11px', color: '#999' }}>
              {confidence}% confidence
              {record.categoryConfirmedBy === 'auto' && ' (auto)'}
            </span>
          </Space>
        );
      }
    },
    {
      title: 'Queue Status',
      dataIndex: 'transcriptionStatus',
      key: 'transcriptionStatus',
      width: '12%',
      render: (status, record) => {
        if (status === 'uploading') {
          return (
            <Space size="small">
              <Spin size="small" />
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Uploading...</Typography.Text>
            </Space>
          );
        }
        if (status === 'processing') {
          return (
            <Space size="small">
              <Spin size="small" />
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Processing...</Typography.Text>
            </Space>
          );
        }
        
        const colorMap = {
          pending: 'default',
          completed: 'green',
          failed: 'red'
        };
        
        // Check if recording has detected language
        const isSpanish = record.detectedLanguage === 'es' || record.detectedLanguage === 'es-US';
        
        return (
          <Space size="small">
            <Tag color={colorMap[status]}>{status}</Tag>
            {isSpanish && <Tag color="blue">Spanish</Tag>}
          </Space>
        );
      }
    },
    {
      title: 'Play',
      key: 'play',
      width: '8%',
      align: 'center',
      render: (_, record) => {
        if (record.isPlaceholder) {
          return null;
        }
        
        return (
          <Button
            type="text"
            size="large"
            icon={
              <FontAwesomeIcon 
                icon={playingId === record._id ? faPauseCircle : faPlayCircle} 
                style={{ fontSize: '20px', color: playingId === record._id ? '#1890ff' : '#666' }}
              />
            }
            onClick={() => handlePlayPause(record)}
          />
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '8%',
      align: 'center',
      render: (_, record) => {
        if (record.isPlaceholder) {
          return null;
        }

        const menuItems = [
          {
            key: 'details',
            label: 'View Details',
            icon: <FontAwesomeIcon icon={faEye} style={{ marginRight: '8px' }} />,
            onClick: () => handleViewDetails(record)
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <FontAwesomeIcon icon={faTrash} style={{ marginRight: '8px' }} />,
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: 'Delete this recording?',
                content: 'This action cannot be undone.',
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: () => handleDelete(record._id)
              });
            }
          }
        ];
        
        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="large"
              icon={
                <FontAwesomeIcon 
                  icon={faEllipsisV} 
                  style={{ fontSize: '16px', color: '#666' }}
                />
              }
            />
          </Dropdown>
        );
      }
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.isPlaceholder || uploadingFiles.includes(record._id)
    })
  };

  return (
    <div>
      {/* Bulk Actions */}
      {selectedRowKeys.length > 0 && (() => {
        // Calculate total duration and estimated cost
        const selectedRecordings = recordings.filter(r => selectedRowKeys.includes(r._id));
        const validForTranscription = selectedRecordings.filter(r => (r.duration || 0) >= 5);
        const tooShort = selectedRecordings.filter(r => (r.duration || 0) < 5);
        
        const totalSeconds = selectedRecordings.reduce((sum, r) => sum + (r.duration || 0), 0);
        const totalMinutes = totalSeconds / 60;
        
        // Format duration as HH:MM:SS
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);
        const durationDisplay = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        
        // Calculate estimated cost (Tier 1: $0.024/minute for first 250k minutes)
        const estimatedCost = (totalMinutes * 0.024).toFixed(2);
        
        return (
          <div style={{ 
            position: 'sticky',
            top: 0,
            zIndex: 10,
            marginBottom: '16px', 
            padding: '12px 16px', 
            background: '#e6f7ff', 
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Space size="middle" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '15px', fontWeight: '500' }}>{selectedRowKeys.length} selected</span>
              <Button
                type="primary"
                icon={<FontAwesomeIcon icon={faBolt} />}
                onClick={handleBulkTranscribe}
                disabled={validForTranscription.length === 0}
                style={{ height: '40px', borderRadius: '8px' }}
              >
                Transcribe {validForTranscription.length > 0 && `(${validForTranscription.length})`}
              </Button>
              {tooShort.length > 0 && (
                <Tag color="orange" style={{ fontSize: '12px' }}>
                  {tooShort.length} too short (&lt; 5s)
                </Tag>
              )}
              <Select
                placeholder="Assign to Campaign"
                style={{ width: 200 }}
                size="large"
                onChange={handleBulkAssignCampaign}
                value={null}
                notFoundContent="No campaigns available"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {campaigns && campaigns.length > 0 ? (
                  campaigns.map(campaign => (
                    <Select.Option key={campaign._id} value={campaign.campaignName || campaign.name}>
                      {campaign.campaignName || campaign.name}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option disabled value="">No campaigns available</Select.Option>
                )}
              </Select>
              <Button
                danger
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={handleBulkDelete}
                style={{ height: '40px', borderRadius: '8px' }}
              >
                Delete Selected
              </Button>
              <Button 
                onClick={() => setSelectedRowKeys([])}
                style={{ height: '40px', borderRadius: '8px' }}
              >
                Clear Selection
              </Button>
              <span style={{ marginLeft: '8px', color: '#1890ff', fontWeight: '500', fontSize: '14px' }}>
                Duration: {durationDisplay}
              </span>
              <span style={{ color: '#52c41a', fontWeight: '500', fontSize: '14px' }}>
                Est. Cost: ${estimatedCost}
              </span>
            </Space>
          </div>
        );
      })()}

      {/* Table */}
      <div style={{ 
        borderRadius: '12px', 
        overflow: 'hidden',
        border: '1px solid #f0f0f0'
      }}>
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={recordings}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            style: { margin: '16px' }
          }}
          onChange={handleTableChange}
          size="small"
        />
      </div>

      {/* Recording Details Modal */}
      <RecordingDetailsModal
        visible={detailsModalVisible}
        recording={selectedRecording}
        onClose={handleDetailsModalClose}
        onUpdate={handleRecordingUpdate}
      />

      {/* Upload Modal */}
      <Modal
        title="Upload Call Recordings"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setFileList([]);
          setUploadResults(null);
          setUploadProgress(0);
        }}
        footer={[
          <Button key="cancel" onClick={() => setUploadModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={handleUpload}
            disabled={uploading || fileList.length === 0}
            loading={uploading}
            style={{ 
              backgroundColor: fileList.length > 0 && !uploading ? '#000' : undefined,
              borderColor: fileList.length > 0 && !uploading ? '#000' : undefined
            }}
          >
            Upload {fileList.length > 0 && `(${fileList.length})`}
          </Button>
        ]}
        width={700}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Upload MP3/WAV call recordings. Filenames should follow format: YYYYMMDD_HHMMSS_extension_phoneNumber_direction.mp3
          </Typography.Paragraph>

          <Upload.Dragger
            multiple
            accept=".mp3,.wav,.m4a"
            fileList={fileList}
            beforeUpload={(file) => {
              // Validate file type
              const isAudio = file.type.startsWith('audio/') ||
                file.name.endsWith('.mp3') ||
                file.name.endsWith('.wav') ||
                file.name.endsWith('.m4a');
              
              if (!isAudio) {
                message.error(`${file.name} is not an audio file`);
                return Upload.LIST_REMOVE;
              }

              // Validate file size (max 50MB)
              const isLt50M = file.size / 1024 / 1024 < 50;
              if (!isLt50M) {
                message.error(`${file.name} must be smaller than 50MB`);
                return Upload.LIST_REMOVE;
              }

              setFileList(prev => [...prev, file]);
              return false; // Prevent auto upload
            }}
            onRemove={(file) => {
              setFileList(prev => prev.filter(f => f.uid !== file.uid));
            }}
          >
            <p className="ant-upload-drag-icon">
              <FontAwesomeIcon icon={faInbox} style={{ fontSize: 48, color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">Click or drag audio files to this area</p>
            <p className="ant-upload-hint">
              Supports MP3, WAV, M4A formats. Maximum 50 files, 50MB each.
            </p>
          </Upload.Dragger>

          {uploading && (
            <Progress 
              percent={uploadProgress} 
              status="active"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
          )}

          {uploadResults && (
            <div>
              {/* Successful uploads */}
              {uploadResults.successful.length > 0 && (
                <Alert
                  message={`Successfully Uploaded (${uploadResults.successful.length})`}
                  type="success"
                  icon={<FontAwesomeIcon icon={faCheckCircle} />}
                  showIcon
                  style={{ marginBottom: '12px' }}
                  description={
                    <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {uploadResults.successful.map((item, idx) => (
                        <div key={idx} style={{ marginBottom: '4px' }}>
                          <Typography.Text style={{ fontSize: '12px' }}>{item.filename}</Typography.Text>
                          {item.recording.campaignName && (
                            <Tag color="blue" style={{ marginLeft: '8px', fontSize: '11px' }}>
                              {item.recording.campaignName}
                            </Tag>
                          )}
                        </div>
                      ))}
                    </div>
                  }
                />
              )}

              {/* Duplicates */}
              {uploadResults.duplicates.length > 0 && (
                <Alert
                  message={`Duplicates Skipped (${uploadResults.duplicates.length})`}
                  type="warning"
                  icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                  showIcon
                  style={{ marginBottom: '12px' }}
                  description={
                    <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                      {uploadResults.duplicates.map((item, idx) => (
                        <div key={idx} style={{ fontSize: '12px', marginBottom: '4px' }}>
                          {item.filename}
                        </div>
                      ))}
                    </div>
                  }
                />
              )}

              {/* Failed uploads */}
              {uploadResults.failed.length > 0 && (
                <Alert
                  message={`Failed (${uploadResults.failed.length})`}
                  type="error"
                  icon={<FontAwesomeIcon icon={faTimesCircle} />}
                  showIcon
                  description={
                    <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                      {uploadResults.failed.map((item, idx) => (
                        <div key={idx} style={{ fontSize: '12px', marginBottom: '4px' }}>
                          <Typography.Text>{item.filename}</Typography.Text>
                          <br />
                          <Typography.Text type="danger" style={{ fontSize: '11px' }}>{item.error}</Typography.Text>
                        </div>
                      ))}
                    </div>
                  }
                />
              )}
            </div>
          )}
        </Space>
      </Modal>

      {/* Filter Drawer */}
      <Drawer
        title="Filter Recordings"
        placement="right"
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        width={400}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Date Range
            </Typography.Text>
            <DatePicker.RangePicker
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates)}
              format="YYYY-MM-DD"
              style={{ width: '100%', height: '48px', borderRadius: '8px' }}
            />
          </div>

          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Campaign
            </Typography.Text>
            <Select
              placeholder="All Campaigns"
              style={{ width: '100%', height: '48px' }}
              value={filters.campaignName}
              onChange={(value) => handleFilterChange('campaignName', value)}
              allowClear
            >
              {campaigns.map(c => (
                <Select.Option key={c._id} value={c.campaignName}>{c.campaignName}</Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Category
            </Typography.Text>
            <Select
              placeholder="All Categories"
              style={{ width: '100%', height: '48px' }}
              value={filters.category}
              onChange={(value) => handleFilterChange('category', value)}
              allowClear
            >
              <Select.Option value="relevant">Relevant</Select.Option>
              <Select.Option value="irrelevant">Irrelevant</Select.Option>
              <Select.Option value="spam">Spam</Select.Option>
              <Select.Option value="unclear">Unclear</Select.Option>
              <Select.Option value="uncategorized">Uncategorized</Select.Option>
            </Select>
          </div>

          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Transcription Status
            </Typography.Text>
            <Select
              placeholder="All Statuses"
              style={{ width: '100%', height: '48px' }}
              value={filters.transcriptionStatus}
              onChange={(value) => handleFilterChange('transcriptionStatus', value)}
              allowClear
            >
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="processing">Processing</Select.Option>
              <Select.Option value="completed">Completed</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
            </Select>
          </div>

          <Button
            type="primary"
            block
            size="large"
            onClick={() => setFilterDrawerVisible(false)}
            style={{ height: '48px', borderRadius: '8px', marginTop: '16px' }}
          >
            Apply Filters
          </Button>

          <Button
            block
            size="large"
            onClick={() => {
              setFilters({
                dateRange: null,
                campaignName: null,
                category: null,
                transcriptionStatus: null
              });
            }}
            style={{ height: '48px', borderRadius: '8px' }}
          >
            Clear All Filters
          </Button>
        </Space>
      </Drawer>
    </div>
  );
});

CallRecordingsTable.displayName = 'CallRecordingsTable';

export default CallRecordingsTable;
